/**
 * Loads a registration's drawn signature as a local object URL (so printing and PDF export
 * don't depend on cross-origin image loading). Resolves to null when there is no signature.
 * Pass `token` (public form) — the edge function returns only that registration's signature.
 */

import { useQuery } from '@tanstack/react-query';
import { getSignatureUrlByToken } from '../publicRegistrationRepository';

async function loadSignature(token: string): Promise<string | null> {
  const url = await getSignatureUrlByToken(token);
  if (!url) return null;
  const res = await fetch(url);
  if (!res.ok) throw new Error('signature fetch failed');
  const objectUrl = URL.createObjectURL(await res.blob());
  // Decode now so the first print/export already has the pixels
  const img = new Image();
  img.src = objectUrl;
  await img.decode().catch(() => undefined);
  return objectUrl;
}

export function useSignatureImage(token: string | undefined, enabled = true): string | null {
  const { data } = useQuery({
    queryKey: ['registration_signature', token],
    queryFn: () => loadSignature(token!),
    enabled: !!token && enabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  return data ?? null;
}
